import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { Word } from 'src/feature/localization/component/atom/Word'
import { useBool } from '../../../../hook/bool/useBool'
import { usePortfolioStore } from '../../store/portfolio.store'
import { PortfolioType, CreatePortfolioDto } from '../../model/portfolio'
import { DeleteConfirmationModal } from '../template/DeleteConfirmationModal'
import { InterestFormModal } from '../template/InterestFormModal'
import { CareerGoalChip } from './CareerGoalChip'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type InterestsCardProps = {
  userId?: string
  initialOpenForm?: boolean
}

export const InterestsCard = observer(({ userId, initialOpenForm }: InterestsCardProps) => {
  const { translation } = useLocalizationStore()
  const portfolioStore = usePortfolioStore()
  const formModalBool = useBool(initialOpenForm)
  const deleteModalBool = useBool()
  const [selectedInterestId, setSelectedInterestId] = useState<string | null>(null)

  useEffect(() => {
    portfolioStore.reset()
    portfolioStore.handleGetPortfolio(userId)
  }, [portfolioStore, userId])

  const { isLoading } = portfolioStore.getGenericFieldsState('handleGetPortfolio')
  const { isLoading: isCreating } = portfolioStore.getGenericFieldsState('handleCreatePortfolioItem')
  const { isLoading: isDeleting } = portfolioStore.getGenericFieldsState('handleDeletePortfolioItem')

  const isOperationInProgress = isCreating || isDeleting

  const handleSubmitInterest = async (data: CreatePortfolioDto) => {
    await portfolioStore.handleCreatePortfolioItem(data)
    formModalBool.makeFalse()
  }

  const handleDeleteInterest = (interestId: string) => {
    setSelectedInterestId(interestId)
    deleteModalBool.makeTrue()
  }

  const handleConfirmDelete = async () => {
    if (selectedInterestId) {
      await portfolioStore.handleDeletePortfolioItem(selectedInterestId, PortfolioType.INTEREST)
      setSelectedInterestId(null)
    }
  }

  const hasInterests = portfolioStore.hasInterests
  const interests = portfolioStore.interests
  const interestsCount = portfolioStore.interestsCount

  return (
    <>
      <AppPaper sx={styles.root}>
        <AppBox width='100%' justifyContent='space-between'>
          <AppBox alignItems='center' gap={5}>
            <Typography variant='h5'>{translation.pages.profileInfo.careerGoalsCard.interests()}</Typography>
            <Word sx={styles.pill}>{interestsCount || '0'}</Word>
          </AppBox>
          {!userId && <AppButton
            fontSize={12}
            textValue={translation.buttons.add()}
            variant="light"
            onClick={formModalBool.makeTrue}
            disabled={isOperationInProgress}
          />}
        </AppBox>
        
        {isLoading ? (
          <AppBox sx={styles.chipContainer}>
            <AppSkeleton
              count={3}
              direction="row"
              rects={[
                { width: 80, height: 32 },
                { width: 100, height: 32 },
                { width: 70, height: 32 },
              ]}
            />
          </AppBox>
        ) : hasInterests ? (
          <AppBox sx={styles.chipContainer}>
            {interests.map(interest => (
              <CareerGoalChip
                key={interest.id}
                text={interest.content}
                showDelete={!userId}
                onDelete={() => handleDeleteInterest(interest.id)}
              />
            ))}
          </AppBox>
        ) : (
          <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>
            {translation.noData()}
          </Caption>
        )}
      </AppPaper>

      <InterestFormModal
        {...formModalBool}
        onSubmit={handleSubmitInterest}
      />

      <DeleteConfirmationModal
        {...deleteModalBool}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title={translation.pages.profile.portfolioModal.deleteConfirmTitle()}
        description={translation.pages.profile.portfolioModal.deleteConfirmDescription()}
      />
    </>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '10px',
    gap: 10,
    width: '100%',
    height: '100%',
    border: 0,
  },
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
  chipContainer: {
    gap: 10,
    flexWrap: 'wrap',
    width: '100%',
    height: 90,
    overflow: 'auto',
  },
}
